<template>
  <div>
    <template v-if="!loading">
      <g-g-app-header>
        WordPress Setup Information
      </g-g-app-header>
      <v-row>
        <v-col cols="12" md="3" class="pb-0">
          <gg-select
            v-model="softproto"
            label="URL Protocol"
            :items="softprotoList"
            item-text="text"
            item-value="value"
          />
        </v-col>
        <v-col cols="12" md="6" class="pb-0">
          <gg-input-label>Domain</gg-input-label>
          <domain-select
            v-model="softdomain"
            flat
            no-label
            hide-details
            outlined
            solo
            @domain-type-selected="setDomainType"
          />
        </v-col>
        <v-col cols="12" md="3" class="pb-0">
          <gg-select
            v-model="language"
            label="Language"
            :items="languages"
            :loading="loadingInfo"
            item-value="value"
            item-text="label"
          />
        </v-col>
        <v-col cols="12" md="6" class="py-0">
          <gg-select
            v-model="customDir"
            label="Installation Path"
            :items="customDirOptions"
            item-value="value"
            item-text="label"
          />
        </v-col>
        <v-col cols="12" md="6" class="py-0">
          <gg-text-field
            v-if="customDir"
            v-model="softdirectory"
            label="Specify Directory"
            name="Directory"
            data-vv-as="Directory"
            v-validate="'required'"
            :error-messages="errors.collect('Directory')"
          />
        </v-col>
        <v-col cols="12" md="6" class="py-0">
          <gg-text-field
            v-model="admin_username"
            label="Admin Username"
            name="admin_username"
            data-vv-as="Admin Username"
            v-validate="'required|excluded:admin,root,administrator'"
            :error-messages="errors.collect('admin_username')"
          />
        </v-col>
        <v-col cols="12" md="6" class="py-0">
          <compact-password
            v-model="admin_pass"
            outlined
            charset="abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMOPRSTUVWXYZ0123456789_"
            autocomplete="new-password"
            label="Admin Password"
            v-validate="'required|min:8'"
            data-vv-name="password_adm"
            data-vv-as="Admin Password"
            :error-messages="errors.collect('password_adm')"
            with-generator
          />
        </v-col>
        <v-col cols="12" md="6" class="py-0">
          <gg-text-field
            v-model="admin_email"
            label="Admin Email"
            name="Admin Email"
            data-vv-as="Admin Email"
            v-validate="'required|email'"
            :error-messages="errors.collect('Admin Email')"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          Your website will be powered by LiteSpeed & LSCache for WordPress for super fast performance.
          <a @click.prevent="showLitespeed = true">More Info</a>
        </v-col>
        <litespeed-info :value="showLitespeed" @update:value="showLitespeed = $event"/>
        <v-col cols="12">
          <gg-btn :loading="loading" @click="install" class="mr-3">Install WordPress</gg-btn>
          <gg-btn secondary :disabled="loading" @click="goBack">Go Back</gg-btn>
        </v-col>
      </v-row>
    </template>

    <v-row v-if="loading">
      <v-col cols="12" class="py-0 d-flex flex-column justify-center align-center">
        <p class="h4 mt-4">Starting something Amazing</p>
        <v-progress-circular indeterminate size="64" width="6" color="primary" />
        <p class="pt-4 h4">This will only take a moment!</p>
      </v-col>
    </v-row>

    <installation-exists-modal
      :softdomain="softdomain"
      :opened="showInstallationExists"
      @close="showInstallationExists = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import Queue from 'async-await-queue'
import amapi from 'Api/amapi'
import DomainSelect from '@/components/Common/DomainSelect'
import CompactPassword from '@/components/Common/password/CompactPassword'
import LitespeedInfo from '../LitespeedInfo'
import InstallationExistsModal from '../Modals/InstallationExistsModal.vue'
import GGAppHeader from '@/components/Elements/GGAppHeader.vue'
import { DOMAIN_TYPES, SOFTPROTO_VALUES } from '@/consts/domains'

export default {
  name: 'StepTwo',
  components: {
    DomainSelect,
    CompactPassword,
    LitespeedInfo,
    GGAppHeader,
    InstallationExistsModal,
  },
  data () {
    return {
      advanced: false,
      loading: false,
      loadingInfo: false,
      customDir: false,
      installing: false,
      showInstallationExists: false,
      selectedDomainType: DOMAIN_TYPES.MAIN,
      customDirOptions: [
        { value: false, label: 'Main Folder' },
        { value: true, label: 'Sub-folder' },
      ],
      showLitespeed: false,
    }
  },
  computed: {
    ...mapGetters({
      hosting: 'hosting', // hosting.id
      softId: 'hosting_wp_install/softId',
      languages: 'hosting_wp_install/languages',
      softdomainVal: 'hosting_wp_install/softdomain',
      softprotoVal: 'hosting_wp_install/softproto',
      softprotoList: 'hosting_wp_install/softprotoValues',
      softdirectoryVal: 'hosting_wp_install/softdirectory',
      languageVal: 'hosting_wp_install/language',
      softdbVal: 'hosting_wp_install/softdb',
      dbusernameVal: 'hosting_wp_install/dbusername',
      dbuserpassVal: 'hosting_wp_install/dbuserpass',
      admin_usernameVal: 'hosting_wp_install/admin_username',
      admin_passVal: 'hosting_wp_install/admin_pass',
      admin_emailVal: 'hosting_wp_install/admin_email',
      site_nameVal: 'hosting_wp_install/site_name',
      site_descVal: 'hosting_wp_install/site_desc',
      availableThemes: 'hosting_wp_install/availableThemes',
      availablePlugins: 'hosting_wp_install/availablePlugins',
      themeVal: 'hosting_wp_install/theme',
      wizardApp: 'hosting_wp_install/wizardApp',
      plugins: 'hosting_wp_install/plugins',
      stepVal: 'hosting_wp_install/step',
      insid: 'hosting_wp_install/insid',
      domainsInstallationRecentlyFailed: 'hosting_wp_install/domainsInstallationRecentlyFailed',
      installations: 'hosting_wp/installations'
    }),
    softdomain: {
      get () {
        return this.softdomainVal
      },
      set (val) {
        this.setSoftdomain(val)
      }
    },
    softproto: {
      get () {
        return this.softprotoVal
      },
      set (val) {
        this.setSoftproto(val)
      }
    },
    softdirectory: {
      get () {
        return this.softdirectoryVal
      },
      set (val) {
        this.setSoftdir(val)
      }
    },
    language: {
      get () {
        return this.languageVal
      },
      set (val) {
        this.setLang(val)
      }
    },
    softdb: {
      get () {
        return this.softdbVal
      },
      set (val) {
        this.setSoftdb(val)
      }
    },
    dbusername: {
      get () {
        return this.dbusernameVal
      },
      set (val) {
        this.setSoftdbuser(val)
      }
    },
    dbuserpass: {
      get () {
        return this.dbuserpassVal
      },
      set (val) {
        this.setSoftdbpwd(val)
      }
    },
    admin_username: {
      get () {
        return this.admin_usernameVal
      },
      set (val) {
        this.setSoftadmin(val)
      }
    },
    admin_pass: {
      get () {
        return this.admin_passVal
      },
      set (val) {
        this.setSoftadminpwd(val)
      }
    },
    admin_email: {
      get () {
        return this.admin_emailVal
      },
      set (val) {
        this.setSoftadminemail(val)
      }
    },
    site_name: {
      get () {
        return this.site_nameVal
      },
      set (val) {
        this.setSoftname(val)
      }
    },
    site_desc: {
      get () {
        return this.site_descVal
      },
      set (val) {
        this.setSoftdesc(val)
      }
    },
  },
  methods: {
    ...mapActions({
      getConfigureOptions: 'hosting_wp_install/getConfigureOptions',
      getInstallationsList: 'hosting_wp/getInstallationsList',
      installWp: 'hosting_wp_install/installWp',
      reportError: 'reportError',
      reportSuccess: 'reportSuccess',
    }),
    ...mapMutations({
      setSoftdomain: 'hosting_wp_install/SET_SOFT_DOMAIN',
      setSoftproto: 'hosting_wp_install/SET_SOFTPROTO',
      setSoftdir: 'hosting_wp_install/SET_SOFT_DIR',
      setLang: 'hosting_wp_install/SET_LANGUAGE',
      setSoftdb: 'hosting_wp_install/SET_SOFT_DB',
      setSoftdbuser: 'hosting_wp_install/SET_SOFT_DB_USER',
      setSoftdbpwd: 'hosting_wp_install/SET_SOFT_DB_PWD',
      setSoftadmin: 'hosting_wp_install/SET_SOFT_ADMIN',
      setSoftadminpwd: 'hosting_wp_install/SET_SOFT_ADMIN_PWD',
      setSoftadminemail: 'hosting_wp_install/SET_SOFT_ADMIN_EMAIL',
      setSoftname: 'hosting_wp_install/SET_SOFT_NAME',
      setSoftdesc: 'hosting_wp_install/SET_SOFT_DESC',
      reset: 'hosting_wp_install/RESET_STATE',
      setStepVal: 'hosting_wp_install/SET_STEP',
    }),
    goBack () {
      this.$emit('set-step', 1)
    },
    getSoftprotoVal (domainType) {
      const softProtoMap = new Map([
        [DOMAIN_TYPES.MAIN, SOFTPROTO_VALUES.https_www.value], // AM-1024
        [DOMAIN_TYPES.SUB, SOFTPROTO_VALUES.https.value],
        [DOMAIN_TYPES.ADDON, SOFTPROTO_VALUES.https_www.value],
      ])

      return softProtoMap.get(domainType) || SOFTPROTO_VALUES.https_www.value
    },
    /**
     * @param domainType DOMAIN_TYPES.MAIN | DOMAIN_TYPES.SUB | DOMAIN_TYPES.ADDON Domain type
     */
    setDomainType (domainType) {
      this.selectedDomainType = domainType

      this.softproto = this.getSoftprotoVal(domainType)
    },
    async toggleAdvanced () {
      if (this.advanced) {
        const valid = await this.$validator.validate('advanced.*')
        if (!valid) {
          return
        }
      }
      this.advanced = !this.advanced
    },
    generateStr (length = 6, charset = 'abcdefghijklmnopqrstuvwxyz') {
      let str = ''
      for (let i = 0, n = charset.length; i < length; ++i) {
        str += charset.charAt(Math.floor(Math.random() * n))
      }
      return str
    },
    async install (evt, overwrite = 0) {
      const valid = await this.$validator.validateAll()
      const validAdvanced = await this.$validator.validate('advanced.*')
      if (!valid || !validAdvanced) {
        return
      }
      if (!overwrite && this.domainsInstallationRecentlyFailed.includes(this.softdomain)) {
        await this.install(null, 1)
        return
      }
      this.loading = true
      this.$emit('set-loading', true)
      try {
        await this.installWp({ overwrite, _username: this.$route.query.user })
        await this.runPluginsQueue()
        if (this.wizardApp !== 'extendify') await this.installTheme()
        this.reportSuccess('Installation complete!')
        this.getInstallationsList(this.$route.query.user)
        this.setStepVal(3)
      } catch (e) {
        if (e.installationExists) {
          this.showInstallationExists = true
        } else {
          this.reportError(e)
        }
      } finally {
        this.setOptions() // reset db etc values to prevent next errors after crash
        this.loading = false
      }
    },
    async installPlugin (slug) {
      const params = {
        method: 'wordpress',
        _username: this.$route.query.user,
        payload: {
          insid: this.insid,
          type: 'plugins',
          slug,
          install: 1,
        }
      }
      try {
        const response = await amapi.softaculous_api(params)
        if (!response.done) {
          throw new Error(response.error[0] ?? 'Plugin error: ' + slug)
        }
      } catch (e) {
        this.reportError(e)
      }
    },
    async runPluginsQueue () {
      this.pluginsQueue = new Queue(1, 200)
      for (const plugin of this.plugins) {
        // eslint-disable-next-line symbol-description
        const task = Symbol()
        await this.pluginsQueue.wait(task, -1)
        this.installPlugin(plugin)
          .catch(e => console.warn(e))
          .finally(() => this.pluginsQueue.end(task))
      }
      return this.pluginsQueue.flush()
    },
    async installTheme () {
      const params = {
        method: 'wordpress',
        _username: this.$route.query.user,
        payload: {
          insid: this.insid,
          type: 'themes',
          slug: this.themeVal,
          install: 1,
        }
      }
      try {
        const response = await amapi.softaculous_api(params)
        if (!response.done) {
          throw new Error(response.error[0] ?? 'Theme error: ' + this.themeVal)
        }
      } catch (e) {
        this.reportError(e)
      }
    },
    setOptions () {
      this.dbusername = this.generateStr()
      this.softdb = this.generateStr()
      this.dbuserpass = this.generateStr(10, 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMOPRSTUVWXYZ0123456789_@#$%')
      this.getConfigureOptions(this.$route.query.user)
    }
  },
  watch: {
    customDir (val) {
      if (!val) {
        this.setSoftdir('')
      }
    },
    stepVal (val) {
      if (val !== 2) {
        return
      }
      this.setOptions()
    }
  },
  mounted () {
    const dict = {
      custom: {
        admin_username: {
          excluded: 'The Admin Username is restricted. Select a unique username.'
        }
      }
    }
    this.$validator.localize('en', dict)
    this.setOptions()
  }
}
</script>
